import {
  withState,
  patchState,
  withMethods,
  signalStoreFeature,
} from '@ngrx/signals';
import { exhaustMap, pipe, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { FirebaseAuthOutput } from './utils/firebase-auth';
import { mapFirebaseAuthErrorToMessage } from './utils/auth-error';
import { withCustomCallState } from '../utils/with-custom-call-state.feature';

type LoginDeps = FirebaseAuthOutput['state'] &
  Pick<FirebaseAuthOutput['methods'], '_googleSignin' | '_login'>;

type LoginUiState = { loginNeedsVerification: boolean };

export function withLoginResources<T extends LoginDeps>(store: T) {
  return signalStoreFeature(
    withCustomCallState('login'),
    withState<LoginUiState>({ loginNeedsVerification: false }),

    withMethods((innerStore) => ({
      googleSignIn: rxMethod<void>(
        pipe(
          tap(() => {
            innerStore._loginSetLoading();
            patchState(innerStore, { loginNeedsVerification: false });
          }),
          exhaustMap(() =>
            store._googleSignin().pipe(
              tapResponse({
                next: () => innerStore._loginSetSuccess(),
                error: (err: unknown) =>
                  innerStore._loginSetError(mapFirebaseAuthErrorToMessage(err)),
              }),
            ),
          ),
        ),
      ),

      login: rxMethod<{ user: string; pass: string }>(
        pipe(
          tap(() => {
            innerStore._loginSetLoading();
            patchState(innerStore, { loginNeedsVerification: false });
          }),
          exhaustMap((creds) =>
            store._login(creds).pipe(
              tapResponse({
                next: (cred) => {
                  if (!cred.user.emailVerified) {
                    innerStore._loginSetError(null);
                    patchState(innerStore, { loginNeedsVerification: true });
                    return;
                  }
                  innerStore._loginSetSuccess();
                },
                error: (err: unknown) =>
                  innerStore._loginSetError(mapFirebaseAuthErrorToMessage(err)),
              }),
            ),
          ),
        ),
      ),

      resetLoginState: () => {
        innerStore._loginResetState();
        patchState(innerStore, { loginNeedsVerification: false });
      },
    })),
  );
}
